import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ActivityHitFragment } from '@oberoninternal/travelbase-website/dist/generated/graphql';
import createImgProps from '@oberoninternal/travelbase-website/dist/utils/createImgProps';
import getBestTimeslot from '@oberoninternal/travelbase-website/dist/utils/getBestTimeslot';
import ActivityTile, { ActivityTileProps } from '@oberoninternal/travelbase-website/dist/components/ActivityTile';
import isSameDay from 'date-fns/isSameDay';
import parseToDateString from '@oberoninternal/travelbase-website/dist/constants/parseToDateString';
import useGetPageSlugByLocale from '@oberoninternal/travelbase-website/dist/hooks/useGetPageSlugByLocale';
import getActivityState from '@oberoninternal/travelbase-website/dist/utils/getActivityTileState';

interface Props extends ActivityTileProps {
    hit: ActivityHitFragment;
    sizes?: string;
}

const EventHit = ({ hit, sizes, ...props }: Props) => {
    const { timeslots } = hit;
    const bestTimeslot = getBestTimeslot(timeslots, hit?.bookingType);

    const intl = useIntl();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();
    const pageSlug = getPageSlugByLocale('ACTIVITY');
    const [timeSlotChildren, start, end] = useMemo(() => {
        const sorted = [...timeslots].sort((a, b) => (a.startDateTime >= b.startDateTime ? 1 : -1));
        const startDate = new Date(sorted.slice(0)[0].startDateTime);
        const endDate = new Date(sorted.slice(-1)[0].startDateTime);

        const options: Intl.DateTimeFormatOptions = {
            day: 'numeric',
            month: 'long',
            year: undefined,
        };

        let dateText = startDate.toLocaleDateString(intl.locale, options);
        if (!isSameDay(startDate, endDate)) {
            dateText += ` - ${endDate.toLocaleDateString(intl.locale, options)}`;
        }

        return [dateText, startDate, endDate];
    }, [intl.locale, timeslots]);

    return (
        <ActivityTile
            title={hit.name}
            videoUrl={hit.videoUrl}
            place={hit.place}
            timeSlotChildren={timeSlotChildren}
            themes={hit.themes.map(t => t.name).join(', ') ?? undefined}
            linkProps={{
                href: {
                    pathname: `/${pageSlug}/${hit.slug}`,
                    query: {
                        startDate: parseToDateString(start),
                        endDate: parseToDateString(end),
                    },
                },
                locale: intl.locale,
            }}
            fullUrl={`${typeof window !== 'undefined' && window.location.origin}/${pageSlug}/${hit.slug}`}
            img={
                hit.listImage && hit.webpListImage ? createImgProps(hit.listImage, hit.webpListImage, sizes) : undefined
            }
            activityState={getActivityState(hit)}
            prices={bestTimeslot?.rateGroup?.rates.map(rate => ({
                name: rate.name,
                price: rate.price,
            }))}
            {...props}
        />
    );
};

export default EventHit;
